import React, { FC, Fragment } from "react";
import { Typography, Box } from "@mui/material";
import classNames from "classnames";
import { IProPercentage, ProFormTabs, getValidProFormTabs } from "./tabs";
import { useUser } from "../../../hooks/useUser";
import { CircularPercentage } from "./formFragments/CircularPercentage";

interface Props {
  inline?: boolean;
  percentage: IProPercentage;
  selectedTab: ProFormTabs;
  updateTab: (e: ProFormTabs) => void;
}

const leftSideStyles = (isSelected?: boolean) => ({
  fontSize: "14px",
  fontWeight: isSelected ? "bold" : "normal",
  // "##72727" / "##fff" are pre-existing invalid literals: the browser drops
  // them, so the unselected tab already inherits the themed color/background.
  // Left as-is — replacing them would change light mode.
  color: isSelected ? "var(--accent-text)" : "##72727",
  paddingX: "12px",
  cursor: "pointer",
  height: "40px",
  display: "flex",
  alignItems: "center",
  borderRadius: 16,
  verticalAlign: "middle",
  backgroundColor: isSelected ? "var(--tint)" : "##fff",
  "&:hover": {
    backgroundColor: "var(--tint)",
    color: "var(--accent-text)",
  },
});

const topStyles = (isSelected?: boolean) => ({
  fontSize: "13px",
  fontWeight: isSelected ? 700 : 500,
  color: isSelected ? "var(--accent-text)" : "var(--text)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
  paddingX: "14px",
  paddingY: "8px",
  borderRadius: "999px",
  border: isSelected ? "1px solid var(--border)" : "1px solid transparent",
  backgroundColor: isSelected ? "var(--tint)" : "transparent",
  boxShadow: isSelected ? "var(--sh-1)" : "none",
  transition: "background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
  "&:hover": {
    backgroundColor: "var(--tint)",
    color: "var(--accent-text)",
    borderColor: "var(--border)",
  },
});

export const TabsPanel: FC<Props> = ({
  inline,
  percentage,
  selectedTab,
  updateTab,
}) => {
  const { role } = useUser();
  if (!role) return <Fragment key="no role found" />;

  return (
    <Box
      className={classNames(
        "bg-surface p-4 overflow-auto rounded-3xl flex h-fit min-w-[220px] sm:min-w-[270px] border border-border-token",
        { "!w-full": inline }
      )}
      flexDirection={inline ? "row" : "column"}
      gap={inline ? 0.75 : 1}
    >
      {getValidProFormTabs(role)
        .filter((e) => e.shouldShow)
        .map((tab, index) => {
          const isSelected = selectedTab === tab.label;
          const tabValue = percentage?.[tab.label];
          return (
            <Box className="flex gap-3 items-center">
              <Typography
                variant="body1"
                sx={inline ? topStyles(isSelected) : leftSideStyles(isSelected)}
                onClick={() => updateTab(tab.label)}
                key={index}
              >
                {tab.label}
              </Typography>
              {!inline && tabValue !== undefined && (
                <CircularPercentage value={tabValue} size={30} />
              )}
            </Box>
          );
        })}
    </Box>
  );
};
